import { TipTypeEnum } from '@anecdotes/design-system/tips';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { User as FirebaseUser, inMemoryPersistence } from '@angular/fire/auth';
import { map } from 'rxjs/operators';
import { LoaderManagerService } from '@anecdotes/design-system/loaders';
import { WindowHelperService } from '@anecdotes/utils';
import { AuthService, FirebaseWrapperService, TenantFacadeService } from '@anecdotes/auth-core';
import { CodeFacade } from '@anecdotes/identity';
import { ActivatedRoute } from '@angular/router';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';

const LINK_EXPIRED_ERROR = 'secret expired';

@Component({
  template: '',
  standalone: true,
})
export class BaseExternalPageComponent implements OnInit {
  get codeId(): string {
    return this.route.snapshot.queryParamMap.get('code_id');
  }

  readonly isAllLoaded$ = new BehaviorSubject<boolean>(false);
  readonly isLinkValid$ = new BehaviorSubject<boolean>(true);
  readonly isLinkExpired$ = new BehaviorSubject<boolean>(false);
  readonly isSubmitted$ = new BehaviorSubject<boolean>(false);

  shouldLogOut = true;
  expirationDate$: Observable<Date>;
  checkboxValue = false;
  approverUser: FirebaseUser;
  approverName: string;
  footerNoBackground$: Observable<boolean>;
  tipTypes = TipTypeEnum;

  @ViewChild('scrollbar', { static: true })
  protected perfectScrollBar: PerfectScrollbarComponent;

  protected readonly codeFacade = inject(CodeFacade);
  protected readonly loaderManager = inject(LoaderManagerService);
  protected readonly windowHelper = inject(WindowHelperService);
  protected readonly firebaseWrapperService = inject(FirebaseWrapperService);
  protected readonly tenantFacade = inject(TenantFacadeService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly authService = inject(AuthService);

  ngOnInit(): void {
    this.expirationDate$ = this.codeFacade.getExpirationDate(this.codeId);
  }

  viewLinkedFile(url: string): void {
    this.windowHelper.openUrlInNewTab(url);
  }

  buildTranslationKey(relativeKey: string): string {
    return `externalApproval.${relativeKey}`;
  }

  async initData(callback?: () => void, allowNoCode = false): Promise<void> {
    // Construct footer observable
    this.footerNoBackground$ = combineLatest([this.isLinkValid$, this.isSubmitted$]).pipe(
      map(([isLinkValid, isSubmitted]) => !isLinkValid || isSubmitted)
    );

    this.loaderManager.show();

    const approverEmail = this.route.snapshot.queryParamMap.get('email');
    try {
      // If the user is already authenticated AND we either allow this external link with already authenticated users
      // OR it's anyway the same authenticated user as the approver email then pass authentication part since it is redundant
      if (
        await this.authService.isAuthenticatedAsync()
        && (allowNoCode || this.firebaseWrapperService.getCurrentUser()?.email === approverEmail)
      ) {
        this.approverUser = this.firebaseWrapperService.getCurrentUser();
        this.shouldLogOut = false;
      } else {
        await this.authenticate();
      }
      await this.getApproverName();
      callback();
    } catch (error) {
      this.isLinkExpired$.next(error?.error?.error_detail?.toLowerCase() === LINK_EXPIRED_ERROR);
      this.loadingFinished();
    }
  }

  protected async authenticate(): Promise<void> {
    const staticCodeId = this.codeId;
    const secretCode = this.route.snapshot.queryParamMap.get('code_secret');
    const { custom_token, tenant_id } = await this.tenantFacade.exchangeTokenAsync(staticCodeId, secretCode);

    this.firebaseWrapperService.setTenantId(tenant_id);

    // The response of the exchange endpoint is the token of the reviewer
    const result = await this.firebaseWrapperService.signInWithToken(custom_token, inMemoryPersistence);
    this.approverUser = result.user;
  }

  protected loadingFinished(): void {
    this.isAllLoaded$.next(true);
    this.isLinkValid$.next(false);
    this.loaderManager.hide();
  }

  protected async getApproverName(): Promise<void> {
    this.approverName = this.approverUser?.displayName || '';
    if (!this.approverName) {
      const user = await this.authService.getUserAsync();
      this.approverName = user ? `${user.first_name} ${user.last_name}` : '';
    }
  }
}
